/**
 * Per-zone-type defaults for the three visit-matching hygiene knobs, mirrored
 * from the backend so the settings modal can NAME the default a zone falls back
 * to ("Default for Checkout Waiting Zone — 30 min") instead of showing a bare
 * number the user has to take on faith.
 *
 * SOURCE OF TRUTH IS PYTHON, NOT THIS FILE:
 *   backend/applicaite/tools/data_preprocessing_functions.py
 *     ZONE_TYPE_DEFAULT_REID_MERGE_SEC   (per-type re-ID merge window)
 *     ZONE_TYPE_DEFAULT_MAX_DUR_SEC      (per-type plausibility cap)
 *     _FALLBACK_REID_MERGE_SEC / _FALLBACK_MAX_DUR_SEC  (unlisted types)
 *   and the flat 10.0s split-visit merge default in
 *   prefetch_zone_settings_for_flows (no per-type table for that one).
 *
 * These tables are DISPLAY-ONLY. The modal never persists a default value — it
 * persists the ABSENCE of the key, and the ETL resolves the fallback itself at
 * match time. Drift between this file and the Python tables therefore mislabels
 * the UI but cannot change what the pipeline computes.
 *
 * Zone-type ids follow the DetectionZoneTypes enum (see constants.js
 * detection_zone_colors_by_typeid, which carries the same 1-11 + 999 mapping).
 */

import { i18n, currentLanguage } from '@/i18n'

// DetectionZoneTypeName -> DetectionZoneTypeId. The modal only has the type
// NAME on props.detectionZone.type, so the lookups below go through this.
export const ZONE_TYPE_ID_BY_NAME = {
    'Entrance Zone': 1,
    'Exit Zone': 2,
    'Combined Entrance Exit Zone': 3,
    'Checkout Waiting Zone': 4,
    'Checkout Interaction Zone': 5,
    'Product Zone': 6,
    'Demo Zone': 7,
    'Display Zone': 8,
    'Info Zone': 9,
    'Service Zone': 10,
    'Pick Up Zone': 11,
    'Cash Register Zone': 999,
}

// Mirrors ZONE_TYPE_DEFAULT_REID_MERGE_SEC. > 0 enables the cross-TrackId
// merge pass for that zone type; queue/interaction zones are the only ones on
// by default, because people stand still there and get occluded, so the
// tracker re-IDs them mid-wait. Product/Demo/Display are deliberately 0 — a
// customer genuinely returning to the same shelf must stay two visits.
export const REID_MERGE_DEFAULT_SEC = {
    1: 0.0,
    2: 0.0,
    3: 0.0,
    4: 2.0,
    5: 2.0,
    6: 0.0,
    7: 0.0,
    8: 0.0,
    // Phase 3: the waiting zones inherit checkout waiting's merge (a queue re-IDs
    // constantly). The staff desks deliberately do NOT merge, matching the register —
    // merging would smooth away the very gaps the unattended measurement reads.
    12: 2.0,
    13: 2.0,
    14: 2.0,
    15: 0.0,
    16: 0.0,
    17: 0.0,
}
export const REID_MERGE_FALLBACK_SEC = 0.0

// Mirrors ZONE_TYPE_DEFAULT_MAX_DUR_SEC. A matched visit longer than this is
// treated as a stuck-TrackId artifact and dropped entirely.
export const MAX_PLAUSIBLE_DEFAULT_SEC = {
    1: 300.0,
    2: 300.0,
    3: 300.0,
    4: 1800.0,
    5: 900.0,
    6: 3600.0,
    7: 3600.0,
    8: 3600.0,
    // Staffed interaction points: a 2h stuck-tracker net, not a measurement.
    9: 7200.0,
    10: 7200.0,
    11: 7200.0,
    // Cash Register is the staff position — a cashier stands there all shift and
    // that is real data. Deliberately left at the 24h artifact guard.
    999: 86400.0,
    // Phase 3 (docs §28.7). Waiting zones take checkout waiting's cap — a queue is a
    // queue. The desk zones are STAFF positions like the register: capping them to a
    // customer-shaped duration would delete the signal the unattended measurement reads.
    12: 1800.0,
    13: 1800.0,
    14: 1800.0,
    15: 86400.0,
    16: 86400.0,
    17: 86400.0,
}
// Only genuinely unknown / new zone type ids land here now.
export const MAX_PLAUSIBLE_FALLBACK_SEC = 86400.0

// Flat, no per-type table on the backend.
export const SPLIT_MERGE_DEFAULT_SEC = 10.0

export function zoneTypeIdFromName(typeName) {
    return ZONE_TYPE_ID_BY_NAME[typeName] ?? null
}

export function reidMergeDefaultSec(typeName) {
    const id = zoneTypeIdFromName(typeName)
    if (id === null) return REID_MERGE_FALLBACK_SEC
    return REID_MERGE_DEFAULT_SEC[id] ?? REID_MERGE_FALLBACK_SEC
}

export function maxPlausibleDefaultSec(typeName) {
    const id = zoneTypeIdFromName(typeName)
    if (id === null) return MAX_PLAUSIBLE_FALLBACK_SEC
    return MAX_PLAUSIBLE_DEFAULT_SEC[id] ?? MAX_PLAUSIBLE_FALLBACK_SEC
}

/**
 * True when this zone type has no entry in the table and is only being carried
 * by the fallback — i.e. nobody has decided what "too long" means for it.
 *
 * Distinct from "the cap happens to be 24h": Cash Register sits at 24h ON
 * PURPOSE, because a cashier stands there all shift. Warning about that one
 * would be telling the user off for a decision we made for them.
 */
export function isMaxPlausibleUnclassified(typeName) {
    const id = zoneTypeIdFromName(typeName)
    return id === null || MAX_PLAUSIBLE_DEFAULT_SEC[id] === undefined
}

/** True for the staff position (Cash Register), where uncapped is intended. */
export function isStaffPositionZoneType(typeName) {
    return zoneTypeIdFromName(typeName) === 999
}

export function splitMergeDefaultSec() {
    return SPLIT_MERGE_DEFAULT_SEC
}

/**
 * Human label for a defaulted duration. Deliberately spells out the 24h
 * fallback as "no limit" rather than "1440 min" — a zone type the backend
 * never classified should read as unguarded, not as a deliberate setting.
 */
export function formatDurationLabel(seconds) {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—'
    const t = i18n.global.t
    // Dutch decimal comma ("2,5 sec"); English keeps String(n) exactly as before.
    const num = (n) => (currentLanguage() === 'nl' ? String(n).replace('.', ',') : String(n))
    if (seconds >= 86400) return t('controls.durationLabel.noLimit')
    if (seconds === 0) return t('controls.durationLabel.off')
    if (seconds < 60) {
        const rounded = Math.round(seconds * 10) / 10
        return `${num(rounded)} sec`
    }
    if (seconds < 3600) return `${Math.round(seconds / 60)} min`
    const hours = Math.round((seconds / 3600) * 10) / 10
    return t('controls.durationLabel.hours', { n: num(hours) })
}
